import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { navigate } from "gatsby";
import useGatsbyAuth from "../../templates/user-account/hook/useGatsbyAuth";
import { RCL } from "../RCL";
import { Icon } from "../Icons";
import { getURLQueries } from "../../utils/helper";
import OktaMenu from "./OktaMenu";
import OktaHeaderSubMenu from "./OktaHeaderSubMenu";
import OktaSigninModal from "./OktaSigninModal";
import { userAccountRoutingPaths } from "../../templates/user-account/config";
import { useUser } from "../../context";
import * as style from "./OktaHeader.module.scss";

const OktaHeader = ({ locale }) => {
	const { authState } = useGatsbyAuth();
	const { user, isLoggedIn, isRegistrationRedirect, setIsRegistrationRedirect } = useUser();

	// Get user data from Okta
	const language = locale?.substring(0, 2) === "fr" ? "fr" : "en";
	const { base, login } = userAccountRoutingPaths[language];
	const signinRoute = `/${language}/${base}/${login}`;
	const [isOpenModal, setLoginOpen] = useState(false);
	const [isAction, setWidgetAction] = useState("");
	const [didUserDismiss, setUserDismiss] = useState(false);

	useEffect(() => {
		// Moved here to avoid SSR error
		const displayModal = getURLQueries("signin");

		if (authState && !isLoggedIn && !isOpenModal && !didUserDismiss) {
			if (
				window.location.href.indexOf(signinRoute) > -1 ||
				displayModal === "true" ||
				displayModal === "1"
			) {
				setLoginOpen(true);
			}
		} else if (window.location.href.indexOf(signinRoute) > -1 && didUserDismiss) {
			navigate(`/${language}`);
		}
	}, [isOpenModal, signinRoute, didUserDismiss, language, isLoggedIn, authState]);

	const oktaTriggerRef = useRef(null);
	const [isOktaSideMenuOpen, setOktaSideMenuToOpen] = useState(false);
	const [isOktaMenuOpen, setOktaMenuToOpen] = useState(false);
	const [isMenuTransitioning, setIsMenuTransitioning] = useState(false);

	const handleTransition = () => {
		setIsMenuTransitioning(!isMenuTransitioning);
	};

	// Handle Drop-down menu toggle and tabbing
	function useComponentVisible(initialIsVisible, setMenuState, trigger, isActive) {
		const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible);
		const menuRef = useRef(null);

		useEffect(() => {
			const handleHideDropdown = event => {
				if (isActive && event.key === "Escape") {
					setIsComponentVisible(false);
					setMenuState(false);

					trigger.current.focus();
				}
			};

			const handleFocusOutside = event => {
				if (event?.target?.offsetParent) {
					if (
						isActive &&
						event.target.offsetParent.closest("#okta-menu") !== menuRef.current.offsetParent
					) {
						setIsComponentVisible(false);
						setMenuState(false);
					}
				}
			};

			document.addEventListener("keydown", handleHideDropdown, true);
			document.addEventListener("focus", handleFocusOutside, true);
			return () => {
				document.removeEventListener("keydown", handleHideDropdown, true);
				document.removeEventListener("focus", handleFocusOutside, true);
			};
		}, [isActive, isComponentVisible, setMenuState, trigger]);

		return { menuRef, isComponentVisible, setIsComponentVisible };
	}

	const { menuRef, isComponentVisible, setIsComponentVisible } = useComponentVisible(
		false,
		setOktaMenuToOpen,
		oktaTriggerRef,
		isOktaMenuOpen
	);

	const handleOktaMenuToggle = (open = false) => {
		if (isMenuTransitioning) {
			return;
		}
		setOktaMenuToOpen(!open);
		setIsComponentVisible(!open);
	};

	const handleOktaSideMenuToggle = (open = false) => {
		if (isMenuTransitioning) {
			return;
		}
		setOktaSideMenuToOpen(!open);
	};

	useEffect(() => {
		if (window.location.pathname !== signinRoute && isRegistrationRedirect) {
			setIsRegistrationRedirect(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={style.menuAccountProfile} id="okta-menu">
			<button
				ref={oktaTriggerRef}
				type="button"
				aria-label={RCL({ searchKey: "okta-header-side-menu-label" })}
				aria-controls="account-side-menu"
				aria-haspopup="true"
				id="accountSignin-side"
				className={style.triggerSideMenu}
				aria-expanded={isOktaSideMenuOpen}
				onClick={() => handleOktaSideMenuToggle(isOktaSideMenuOpen)}>
				<Icon name="my-account--o" />
			</button>
			<button
				ref={oktaTriggerRef}
				type="button"
				aria-label={RCL({
					searchKey: "okta-header-drop-down-menu-label",
				})}
				aria-controls="account-flyout-menu"
				aria-haspopup="true"
				id="accountSignin"
				className={style.triggerDDMenu}
				aria-expanded={isComponentVisible && isOktaMenuOpen}
				onClick={() => handleOktaMenuToggle(isComponentVisible && isOktaMenuOpen)}>
				<Icon name="my-account--o" />
				{user && isLoggedIn && (
					<div className={style.ssoTriggerContainer}>
						<span className={style.profileText}>
							<span className={style.greeting}>
								<RCL searchKey="okta-header-welcome" />
							</span>
							{user?.profile?.firstName && (
								<span className={style.userName}>{user?.profile?.firstName}</span>
							)}
							<Icon className={style.icon} name="arrow-down" />
						</span>
					</div>
				)}
			</button>
			<div
				id="account-flyout-menu"
				ref={menuRef}
				className={style.DropdownMenu}
				tabIndex="-1"
				data-expanded={isComponentVisible && isOktaMenuOpen}
				aria-hidden={!(isComponentVisible && isOktaMenuOpen)}>
				<Icon name="caret-up" className={style.tail} />
				<OktaMenu
					locale={locale}
					isUserLoggedIn={isLoggedIn}
					isOktaMenuOpen={isOktaMenuOpen}
					setLoginOpen={setLoginOpen}
					setWidgetAction={setWidgetAction}
					setOktaMenuToOpen={setOktaMenuToOpen}
					isAgent={user?.profile?.agentGlobalId}
				/>
			</div>
			<OktaHeaderSubMenu
				isLoggedIn={isLoggedIn}
				isOktaMenuOpen={isOktaSideMenuOpen}
				handleOktaSideMenuToggle={handleOktaSideMenuToggle}
				transitionState={isMenuTransitioning}
				handleTransition={handleTransition}
				locale={locale}
				className={style.headerDisplaySideMenu}
				setLoginOpen={setLoginOpen}
				setWidgetAction={setWidgetAction}
				setOktaMenuToOpen={setOktaSideMenuToOpen}
				isAgent={user?.profile?.agentGlobalId}
			/>

			{authState &&
				!authState.isAuthenticated &&
				!isLoggedIn &&
				isOpenModal &&
				!isRegistrationRedirect && (
					<OktaSigninModal
						setLoginOpen={setLoginOpen}
						isOpenModal={isOpenModal}
						setUserDismiss={setUserDismiss}
						language={language}
						setWidgetAction={setWidgetAction}
						isAction={isAction}
						customStyles={{
							modalOverlay: style.modalOverlay,
							modalContent: style.modalContent,
							modalContainer: style.modalContainer,
							modalClose: style.modalClose,
						}}
					/>
				)}
		</div>
	);
};

OktaHeader.propTypes = {
	locale: PropTypes.oneOf(["en-CA", "fr-CA"]).isRequired,
};

export default OktaHeader;
export { OktaHeader };
